/**
 * Home for someone just starting: the four steps to the first registrations, and where the live parts will be once
 * people come. Ported from the prototype's v-fresh.js.
 */
import { type ReactNode, useState } from "react";
import { useNavigate } from "react-router";
import type { HomeResponse, SetupStep } from "../../../shared/types";
import { BOARD_NEW_PATH, boardPath } from "../../board/paths";
import { InviteSheet, inviteTargetOf } from "../../components/InviteSheet";
import { Button } from "../../components/ui/Button";
import { Icon } from "../../components/ui/Icon";
import { useToast } from "../../components/ui/Toast";
import { clock, longDay } from "../../lib/when";
import { useCopy } from "./parts";

const CHIPS: Array<{ key: SetupStep | "email"; label: string }> = [
  { key: "look", label: "Your look" },
  { key: "email", label: "Your email" },
  { key: "address", label: "Web address" },
  { key: "tracking", label: "Tracking, optional" },
];

export function GettingStarted({ data }: { data: HomeResponse }) {
  const navigate = useNavigate();
  const toast = useToast();
  const copy = useCopy();
  const [copied, setCopied] = useState(false);
  const [inviting, setInviting] = useState(false);
  const setup = data.setup;
  const f = data.first_room;
  const setupDone = CHIPS.every((c) => setup[c.key]);
  const shared = !!f && (f.shared || copied);
  const done = [setupDone, !!f, !!(f?.published && shared), false];
  const cur = done.indexOf(false);
  const when = f ? (f.live ? (f.starts_at ? `live, ${longDay(f.starts_at)}, ${clock(f.starts_at)}` : "a live room") : "a recording people watch any time") : "";
  const copyFirst = () => {
    if (!f) return;
    copy(f.register_url);
    setCopied(true);
  };

  const steps: Array<{ t: string; d?: string; x?: ReactNode; a?: ReactNode }> = [
    {
      t: "Finish setting up",
      x: (
        <div className="row g6 wrap mt8">
          {CHIPS.map((c) => (
            <span key={c.key} className={setup[c.key] ? "schip on" : "schip"}>
              {setup[c.key] ? <Icon name="check" /> : null}
              {c.label}
            </span>
          ))}
        </div>
      ),
      a: setupDone ? null : (
        <Button size="s" variant={cur === 0 ? "p" : "plain"} after="right" onClick={() => navigate(`/setup/${CHIPS.find((c) => !setup[c.key])?.key ?? ""}`)}>
          {setup.email ? "Finish setup" : "Set up email"}
        </Button>
      ),
    },
    {
      t: "Make your first room",
      d: f ? `${f.title} · ${f.published ? "" : "draft, "}${when}` : undefined,
      a: f ? (
        <Button size="s" onClick={() => navigate(f.published ? `/rooms/${f.id}` : boardPath(f.id))}>
          Open it
        </Button>
      ) : (
        <div className="row g6">
          <Button size="s" variant={cur === 1 ? "p" : "plain"} icon="upload" onClick={() => navigate(`${BOARD_NEW_PATH}?from=recording`)}>
            From a recording
          </Button>
          <Button size="s" icon="live" onClick={() => navigate(`${BOARD_NEW_PATH}?from=live`)}>
            A live room
          </Button>
        </div>
      ),
    },
    {
      t: "Share its sign-up page",
      d: f && !f.published ? "Draft. Publish it to get its link." : shared ? "Shared" : undefined,
      x:
        f?.published ? (
          <div className="linkchip mt8">
            <Icon name="globe" />
            <span>{f.register_url}</span>
          </div>
        ) : null,
      a:
        f && !f.published ? (
          <Button size="s" variant={cur === 2 ? "p" : "plain"} onClick={() => navigate(boardPath(f.id))}>
            Open the draft
          </Button>
        ) : f ? (
          <div className="row g6">
            <Button size="s" variant={cur === 2 ? "p" : "plain"} icon="link" onClick={copyFirst}>
              Copy link
            </Button>
            <Button size="s" icon="userplus" onClick={() => setInviting(true)}>
              Invite
            </Button>
          </div>
        ) : null,
    },
    { t: "Your first registrations", d: f ? "0 so far" : undefined },
  ];

  return (
    <div className="grid g12" style={{ alignItems: "start" }}>
      <section className="card s7 gsteps">
        <div className="card-h">
          <div>
            <h2>Getting to your first registrations</h2>
            <div className="sub">{`${done.filter(Boolean).length} of 4 done`}</div>
          </div>
          <div className="gprog" aria-hidden="true">
            {done.map((d, i) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: the four steps never move
              <i key={i} className={d ? "on" : i === cur ? "cur" : ""} />
            ))}
          </div>
        </div>
        <div className="card-b" style={{ paddingTop: 12 }}>
          {steps.map((s, i) => (
            <div key={s.t} className={`gstep ${done[i] ? "done" : i === cur ? "cur" : "later"}`}>
              <span className="gn">{done[i] ? <Icon name="check" /> : i + 1}</span>
              <div className="sp" style={{ minWidth: 0 }}>
                <div className="gt">{s.t}</div>
                {s.d ? <div className="gd">{s.d}</div> : null}
                {s.x}
              </div>
              {s.a ?? <span />}
            </div>
          ))}
        </div>
      </section>
      <div className="s5 col" style={{ gap: 20 }}>
        <section className="card">
          <div className="card-h">
            <h2>Right now</h2>
            <span className="pill">Quiet for now</span>
          </div>
          <div className="card-b">
            <div className="calm">
              <div className="eic sm">
                <Icon name="eye" />
              </div>
              <div>Who's watching, as it happens.</div>
            </div>
          </div>
        </section>
        <section className="card">
          <div className="card-h">
            <h2>Needs a word from you</h2>
            <span className="pill">No one yet</span>
          </div>
          <div className="card-b">
            <div className="calm">
              <div className="eic sm">
                <Icon name="chat" />
              </div>
              <div>Questions and offer clicks wait here.</div>
            </div>
          </div>
        </section>
      </div>
      {inviting && f ? (
        <InviteSheet
          webinar={inviteTargetOf({ id: f.id, title: f.title })}
          onClose={() => setInviting(false)}
          onSent={() => {
            setInviting(false);
            setCopied(true);
          }}
          onToast={(m) => toast(m)}
        />
      ) : null}
    </div>
  );
}
